import { ApolloProvider, from, split } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloClient, createHttpLink } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { ReactNode, useEffect, useMemo } from 'react';
import { createErrorLink } from '../handlers/errorHander';
import { SSELink } from '../links/SSELink';
import { ResilientSubscriptionLink } from '../links/resilientSubscriptionLink';
import { createWsLink } from '../links/wsLink';

export interface GraphQLProviderProps {
	children: ReactNode;
	uri: string;
	token?: string;
	/**
	 * Identifier for the calling app — sent on every request as the
	 * `x-app-audience` header. The auth subgraph uses this to scope
	 * refresh-token cookies per app (e.g. `codezo_refresh_token_admin`)
	 * so multiple apps on the same hostname can keep independent sessions.
	 *
	 * Required for any app that needs to log in / refresh / log out. If
	 * omitted, those mutations will be rejected by the backend.
	 */
	audience?: string;
	onUnauthorized?: () => void;
	/**
	 * A mutation the backend refused because this session is an admin's
	 * read-only guest preview. The message names the operation and is written
	 * to be shown as-is. Apps that can never be in a preview session pass
	 * nothing.
	 */
	onGuestPreviewBlocked?: (message: string) => void;
	/**
	 * How subscriptions travel. `ws` (default): one WebSocket per tab, every
	 * subscription a message on it. `sse`: a connection per subscription.
	 *
	 * WebSocket is the default because SSE costs a connection per subscription
	 * and browsers allow about six per host over HTTP/1.1, shared by every tab —
	 * a few live features across two apps exhausted it and ordinary requests
	 * hung. It needs the gateway's socket handling (token from connection
	 * params, forwarded to the services) and a host that allows WebSocket
	 * upgrades. Apps set it from VITE_SUBSCRIPTION_TRANSPORT via AuthGraphQLProvider.
	 */
	subscriptionTransport?: 'ws' | 'sse';
}

/**
 * Shared GraphQL Provider that creates and configures Apollo Client
 *
 * Usage:
 * - Without auth: <GraphQLProvider>{children}</GraphQLProvider>
 * - With auth: <GraphQLProvider getAuthToken={() => token}>{children}</GraphQLProvider>
 * - With custom endpoint: <GraphQLProvider uri="http://localhost:5000/graphql">{children}</GraphQLProvider>
 * - With error handling: <GraphQLProvider onUnauthorized={() => logout()}>{children}</GraphQLProvider>
 */
export function GraphQLProvider({
	children,
	uri,
	token,
	audience,
	onUnauthorized,
	onGuestPreviewBlocked,
	subscriptionTransport = 'ws',
}: GraphQLProviderProps) {
	// Memoize the Apollo Client to prevent recreation on every render
	const { client, wsClient } = useMemo(() => {
		const httpLink = createHttpLink({
			uri,
			// Send/receive HttpOnly cookies cross-origin (e.g. refresh-token cookie).
			// Requires the backend CORS to allowlist the frontend origin and set
			// `Access-Control-Allow-Credentials: true`.
			credentials: 'include',
		});

		// Auth Link for JWT tokens
		const authLink = setContext((_, { headers }) => {
			return {
				headers: {
					...headers,
					...(token && { authorization: `Bearer ${token}` }),
					...(audience && { 'x-app-audience': audience }),
				},
			};
		});

		const errorLink = createErrorLink(onUnauthorized ?? (() => {}), onGuestPreviewBlocked);

		/* Subscriptions: one WebSocket per tab by default, or SSE (a connection
		   per subscription) with VITE_SUBSCRIPTION_TRANSPORT=sse. The socket
		   carries the headers as connection params — see `createWsLink`. */
		const authParams = () => {
			const params: Record<string, string> = {};
			if (token) params.authorization = `Bearer ${token}`;
			if (audience) params['x-app-audience'] = audience;
			return params;
		};
		const ws =
			subscriptionTransport === 'ws'
				? createWsLink({ uri, connectionParams: authParams })
				: null;
		// Re-opens a subscription the backend ended by restarting — either transport.
		const subscriptionLink = from([
			new ResilientSubscriptionLink(),
			ws ? ws.link : new SSELink({ uri, headers: authParams }),
		]);

		// Subscriptions over the chosen transport, queries and mutations over HTTP
		const splitLink = split(
			({ query }) => {
				const definition = getMainDefinition(query);
				return (
					definition.kind === 'OperationDefinition' &&
					definition.operation === 'subscription'
				);
			},
			subscriptionLink,
			from([authLink, httpLink])
		);

		// Configure Apollo Client
		const apolloClient = new ApolloClient({
			link: from([errorLink, splitLink]),
			cache: new InMemoryCache(),
			defaultOptions: {
				watchQuery: {
					errorPolicy: 'all',
					notifyOnNetworkStatusChange: true,
				},
				query: {
					errorPolicy: 'all',
					notifyOnNetworkStatusChange: true,
				},
			},
		});
		return { client: apolloClient, wsClient: ws?.client ?? null };
	}, [uri, token, audience, onUnauthorized, onGuestPreviewBlocked, subscriptionTransport]);

	// A rebuilt client (new token, say) brings its own socket; close the old one.
	useEffect(() => () => void wsClient?.dispose(), [wsClient]);

	return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
